import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Save,
  User,
  Briefcase,
  Mail,
  Phone,
  Globe,
  Linkedin,
  MapPin,
  Upload,
  QrCode,
  ExternalLink,
  Trash2 } from
'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle } from
'@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { employeeAPI } from '@/lib/api';

const EmployeeForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = !!id;

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEditing);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [qrData, setQrData] = useState(null);

  const [form, setForm] = useState({
    full_name: '',
    company_name: '',
    job_title: '',
    email: '',
    phone: '',
    mobile: '',
    linkedin: '',
    website: '',
    address: '',
    profile_photo: '',
    is_active: true
  });

  useEffect(() => {
    if (isEditing) {
      fetchEmployee();
    } else {
      // Fetch settings to auto-populate company name for new employees
      fetchSettings();
    }
  }, [id]);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setForm(prev => ({ ...prev, company_name: response.data.company_name }));
    } catch (error) {
      console.error('Failed to load settings:', error);
    }
  };

  const fetchEmployee = async () => {
    try {
      const response = await employeeAPI.get(id);
      setForm(response.data);
      // Also fetch QR code
      const qrResponse = await employeeAPI.getQR(id);
      setQrData(qrResponse.data);
    } catch (error) {
      toast.error('Failed to load employee');
      navigate('/admin/employees');
    } finally {
      setFetching(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isEditing) {
        await employeeAPI.update(id, form);
        toast.success('Employee updated successfully');
      } else {
        await employeeAPI.create(form);
        toast.success('Employee created successfully');
      }
      navigate('/admin/employees');
    } catch (error) {
      console.error('Save error:', error.response?.data);
      const errorMsg = error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to save employee';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await employeeAPI.delete(id);
      toast.success('Employee deleted');
      navigate('/admin/employees');
    } catch (error) {
      toast.error('Failed to delete employee');
    }
    setDeleteDialog(false);
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setForm({ ...form, profile_photo: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  if (fetching) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>);

  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/admin/employees">
          <Button variant="ghost" size="icon" className="rounded-xl">
            <ArrowLeft className="w-5 h-5" />
          </Button>
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            {isEditing ? 'Edit Employee' : 'Add New Employee'}
          </h1>
          <p className="text-slate-600 mt-1">
            {isEditing ? 'Update employee card details' : 'Create a new digital business card'}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="lg:col-span-2">

          <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-slate-100 p-6 space-y-6">
            {/* Profile Photo */}
            <div className="flex items-start gap-6">
              <div className="relative">
                <label htmlFor="photo-upload" className="block cursor-pointer">
                  {form.profile_photo ? (
                    <div className="w-32 h-32 rounded-full overflow-hidden border-2 border-slate-200">
                      <img 
                        src={form.profile_photo} 
                        alt="Profile" 
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ) : (
                    <div className="w-32 h-32 rounded-full border-2 border-dashed border-slate-300 flex flex-col items-center justify-center gap-2 text-slate-400 hover:border-slate-400 hover:text-slate-500 transition-colors bg-slate-50">
                      <Upload className="w-8 h-8" />
                      <span className="text-xs font-medium">Upload</span>
                    </div>
                  )}
                </label>
                <input
                  id="photo-upload"
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoChange}
                  className="hidden"
                />
              </div>
              <div className="flex-1 pt-2">
                <p className="font-medium text-slate-900 mb-1">Profile Photo</p>
                <p className="text-sm text-slate-500">
                  Upload a professional photo. Square images work best.
                </p>
                {form.profile_photo && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="mt-2 text-red-600"
                    onClick={() => setForm({ ...form, profile_photo: '' })}
                  >
                    Remove Photo
                  </Button>
                )}
              </div>
            </div>

            <hr className="border-slate-100" />

            {/* Basic Info */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="form-group">
                <Label htmlFor="full_name" className="form-label">
                  <User className="w-4 h-4 inline mr-2" />
                  Full Name *
                </Label>
                <Input
                  id="full_name"
                  value={form.full_name}
                  onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                  placeholder="John Doe"
                  required
                  data-testid="input-full-name" className="flex h-9 w-full border border-input transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 text-base md:text-sm !mt-[8px] !pt-[4px] !pb-[4px] !pl-[12px] !pr-[12px] rounded-md shadow-sm bg-transparent" />
              
              </div>
              <div className="form-group">
                <Label htmlFor="company_name" className="form-label">
                  <Briefcase className="w-4 h-4 inline mr-2" />
                  Company Name
                </Label>
                <Input
                  id="company_name"
                  value={form.company_name || ''}
                  onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                  placeholder="T.C. Christoforou Ltd"
                  data-testid="input-company-name" className="flex h-9 w-full border border-input transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 text-base md:text-sm !mt-[8px] !pt-[4px] !pb-[4px] !pl-[12px] !pr-[12px] rounded-md shadow-sm bg-transparent" />

              </div>
              <div className="form-group">
                <Label htmlFor="job_title" className="form-label">
                  <Briefcase className="w-4 h-4 inline mr-2" />
                  Job Title *
                </Label>
                <Input
                  id="job_title"
                  value={form.job_title}
                  onChange={(e) => setForm({ ...form, job_title: e.target.value })}
                  placeholder="Sales Manager"
                  required
                  data-testid="input-job-title" className="flex h-9 w-full border border-input transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 text-base md:text-sm !mt-[8px] !pt-[4px] !pb-[4px] !pl-[12px] !pr-[12px] rounded-md shadow-sm bg-transparent" />

              </div>
            </div>

            {/* Contact Info */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="form-group">
                <Label htmlFor="email" className="form-label">
                  <Mail className="w-4 h-4 inline mr-2" />
                  Email Address *
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="example@fecint.com"
                  required
                  data-testid="input-email" className="flex h-9 w-full border border-input transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 text-base md:text-sm !my-[8px] !pt-[4px] !pb-[4px] !pl-[12px] !pr-[12px] rounded-md shadow-sm bg-transparent" />

              </div>
              <div className="form-group">
                <Label htmlFor="phone" className="form-label">
                  <Phone className="w-4 h-4 inline mr-2" />
                  Office Phone *
                </Label>
                <Input
                  id="phone"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="+357 "
                  required
                  data-testid="input-phone" className="flex h-9 w-full border border-input transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 text-base md:text-sm !mt-[8px] !pt-[4px] !pb-[4px] !pl-[12px] !pr-[12px] rounded-md shadow-sm bg-transparent" />

              </div>
              <div className="form-group">
                <Label htmlFor="mobile" className="form-label">
                  <Phone className="w-4 h-4 inline mr-2" />
                  Mobile Number <span className="text-slate-400 text-xs">(Optional)</span>
                </Label>
                <Input
                  id="mobile"
                  value={form.mobile || ''}
                  onChange={(e) => setForm({ ...form, mobile: e.target.value })}
                  placeholder="+357 "
                  data-testid="input-mobile" className="flex h-9 w-full border border-input transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 text-base md:text-sm !mt-[8px] !pt-[4px] !pb-[4px] !pl-[12px] !pr-[12px] rounded-md shadow-sm bg-transparent" />

              </div>
            </div>

            {/* Links */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="form-group">
                <Label htmlFor="website" className="peer-disabled:cursor-not-allowed peer-disabled:opacity-70 form-label text-sm font-medium leading-none !mb-[0px] !pb-[0px]">
                  <Globe className="w-4 h-4 inline mr-2" />
                  Website
                </Label>
                <Input
                  id="website"
                  value={form.website}
                  onChange={(e) => setForm({ ...form, website: e.target.value })}
                  placeholder="https://www.fecint.com"
                  data-testid="input-website" className="flex h-9 w-full border border-input transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 text-base md:text-sm !mt-[8px] !pt-[4px] !pb-[4px] !pl-[12px] !pr-[12px] rounded-md shadow-sm bg-transparent" />

              </div>
              <div className="form-group">
                <Label htmlFor="linkedin" className="form-label">
                  <Linkedin className="w-4 h-4 inline mr-2" />
                  LinkedIn Profile
                </Label>
                <Input
                  id="linkedin"
                  value={form.linkedin}
                  onChange={(e) => setForm({ ...form, linkedin: e.target.value })}
                  placeholder="https://linkedin.com/name"
                  data-testid="input-linkedin" className="flex h-9 w-full border border-input transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 text-base md:text-sm !mt-[8px] !pt-[4px] !pb-[4px] !pl-[12px] !pr-[12px] rounded-md shadow-sm bg-transparent" />

              </div>
            </div>

            {/* Address */}
            <div className="form-group">
              <Label htmlFor="address" className="form-label">
                <MapPin className="w-4 h-4 inline mr-2" />
                Office Address
              </Label>
              <Input
                id="address"
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                placeholder=""
                data-testid="input-address" className="flex h-9 w-full border border-input transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 text-base md:text-sm !mt-[8px] !pt-[4px] !pb-[4px] !pl-[12px] !pr-[12px] rounded-md shadow-sm bg-transparent" />

            </div>

            {/* Status Toggle */}
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
              <div>
                <p className="font-medium text-slate-900">Card Status</p>
                <p className="text-sm text-slate-500">
                  {form.is_active ? 'Card is publicly visible' : 'Card is hidden from public'}
                </p>
              </div>
              <Switch
                checked={form.is_active}
                onCheckedChange={(checked) => setForm({ ...form, is_active: checked })}
                data-testid="toggle-active" />

            </div>

            {/* Actions */}
            <div className="flex items-center justify-between pt-4 border-t border-slate-100">
              {isEditing &&
              <Button
                type="button"
                variant="ghost"
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                onClick={() => setDeleteDialog(true)}
                data-testid="delete-btn">

                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
              }
              <div className={`flex gap-3 ${!isEditing ? 'ml-auto' : ''}`}>
                <Link to="/admin/employees">
                  <Button type="button" variant="outline">Cancel</Button>
                </Link>
                <Button
                  type="submit"
                  className="btn-primary"
                  disabled={loading}
                  data-testid="save-btn">

                  {loading && <div className="spinner mr-2" />}
                  <Save className="w-4 h-4 mr-2" />
                  {isEditing ? 'Save Changes' : 'Create Card'}
                </Button>
              </div>
            </div>
          </form>
        </motion.div>

        {/* Sidebar - QR Code Preview */}
        {isEditing && qrData && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="space-y-4"
          >
            <div className="bg-white rounded-2xl border border-slate-100 p-6">
              <h3 className="font-semibold text-slate-900 mb-4">QR Code</h3>
              <div className="flex flex-col items-center gap-4">
                <div className="bg-white p-4 rounded-xl border border-slate-200">
                  <img
                    src={qrData.qr_code && qrData.qr_code_type === 'svg' 
                      ? `data:image/svg+xml;base64,${qrData.qr_code}` 
                      : (qrData.qr_code_url || `https://api.qrserver.com/v1/create-qr-code/?size=1200x1200&data=${encodeURIComponent(qrData.card_url)}`)}
                    alt="QR Code"
                    className="w-40 h-40"
                    crossOrigin="anonymous"
                  />
                </div>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={async () => {
                    try {
                      if (qrData.qr_code && qrData.qr_code_type === 'svg') {
                        // Decode base64 SVG and convert to PNG
                        const svgData = atob(qrData.qr_code);
                        const blob = new Blob([svgData], { type: 'image/svg+xml' });
                        const qrUrl = URL.createObjectURL(blob);
                        
                        const img = new Image();
                        img.onload = () => {
                          const canvas = document.createElement('canvas');
                          canvas.width = 1200;
                          canvas.height = 1200;
                          const ctx = canvas.getContext('2d');
                          ctx.fillStyle = 'white';
                          ctx.fillRect(0, 0, 1200, 1200);
                          ctx.drawImage(img, 0, 0, 1200, 1200);
                          
                          canvas.toBlob((blob) => {
                            const link = document.createElement('a');
                            link.download = `${form.full_name.replace(/\s+/g, '_')}_QR_1200x1200.png`;
                            link.href = URL.createObjectURL(blob);
                            link.click();
                            URL.revokeObjectURL(link.href);
                            URL.revokeObjectURL(qrUrl);
                          }, 'image/png');
                        };
                        img.src = qrUrl;
                      } else {
                        // Fallback to external API
                        const qrUrl = qrData.qr_code_url || `https://api.qrserver.com/v1/create-qr-code/?size=1200x1200&data=${encodeURIComponent(qrData.card_url)}`;
                        window.open(qrUrl, '_blank');
                      }
                    } catch (error) {
                      console.error('QR download error:', error);
                    }
                  }}
                >
                  <QrCode className="w-4 h-4 mr-2" />
                  Download QR
                </Button>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-slate-100 p-6">
              <h3 className="font-semibold text-slate-900 mb-4">Card Preview</h3>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => window.open(`/card/${form.slug}`, '_blank')}
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Open Public Card
              </Button>
              <p className="text-xs text-slate-500 mt-3 text-center break-all">
                {qrData.card_url}
              </p>
            </div>
          </motion.div>
        )}
      </div>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialog} onOpenChange={setDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Employee Card</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {form.full_name}'s card? 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700">

              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>);

};

export default EmployeeForm;